import React from 'react';
import { Button, Checkbox, Input, Modal, Popconfirm, Space } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { STATUS_TEXT } from '../../utils/orthoToolbox';
import OrthoAssetsModal from '../OrthoAssetsModal';

/**
 * 航拍建模管理弹窗
 */
export default function OrthoTaskManageModal({
  open,
  title,
  tasks,
  activeId,
  activeTask,
  busy,
  code,
  wantGlb,
  purgeInboxOnSuccess,
  assetsModalOpen,
  holdPanelOpenRef,
  onClose,
  onTitleChange,
  onCreateTask,
  onSelectTask,
  onDeleteTask,
  onCodeChange,
  onConfirmCode,
  onAbortModeling,
  onOpenAssets,
  onCloseAssets,
  onAssetsChanged,
  onStartRenderAoi,
  onRequestStart,
  onWantGlbChange,
  onPurgeChange,
  onMarkReady,
  onUnready,
  onWithdraw,
  onPrepareRemodel,
  onClearAssets
}) {
  return (
    <Modal
      open={open}
      title="航拍建模管理"
      onCancel={onClose}
      footer={null}
      width={560}
      destroyOnClose={false}
      styles={{ body: { maxHeight: '72vh', overflow: 'auto' } }}
    >
      <div className="zhxt-ortho-muted" style={{ marginBottom: 8 }}>
        新建任务 → 上传航片 → 框选渲染区 → 申请启动。功能与原先一致，仅移入弹窗。
        成品删除与 OSS 清理请到管理后台「叠加图层」。
      </div>
      <Space.Compact style={{ width: '100%', marginTop: 10 }}>
        <Input
          size="middle"
          placeholder="新任务名称"
          value={title}
          onChange={(e) => onTitleChange(e.target.value)}
          onPressEnter={onCreateTask}
        />
        <Button size="middle" type="default" loading={busy} onClick={onCreateTask}>新建</Button>
      </Space.Compact>

      {(tasks || []).length > 0 && (
        <div className="zhxt-ortho-task-list">
          {(tasks || []).slice(0, 8).map((t) => (
            <div
              key={t.id}
              className={`zhxt-ortho-task ${activeId === t.id ? 'active' : ''}`}
              onClick={() => onSelectTask(t.id)}
              onKeyDown={() => {}}
              role="button"
              tabIndex={0}
            >
              <div className="zhxt-ortho-task-row">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="zhxt-ortho-task-title">#{t.id} {t.title || '未命名'}</div>
                  <div className="zhxt-ortho-muted">
                    {STATUS_TEXT[t.status] || t.status}
                    {t.assetCount != null ? ` · ${t.assetCount} 张` : ''}
                  </div>
                </div>
                {!['provisioning', 'modeling'].includes(t.status) && (
                  <Popconfirm
                    title={`删除任务 #${t.id}？`}
                    description="删除后不可恢复"
                    okText="删除"
                    cancelText="取消"
                    okButtonProps={{ danger: true }}
                    onConfirm={(e) => {
                      e?.stopPropagation?.();
                      return onDeleteTask(t.id);
                    }}
                    onCancel={(e) => e?.stopPropagation?.()}
                  >
                    <Button
                      type="text"
                      size="small"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={(e) => e.stopPropagation()}
                      title="删除任务"
                    />
                  </Popconfirm>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTask && (
        <>
          <div className="zhxt-ortho-hero">
            <div className="zhxt-ortho-hero-top">
              <div className="zhxt-ortho-hero-title">
                #{activeTask.id} {activeTask.title || '未命名任务'}
              </div>
              <span className={`zhxt-ortho-badge zhxt-ortho-badge--${activeTask.status}`}>
                {STATUS_TEXT[activeTask.status] || activeTask.status}
              </span>
            </div>
            <div className="zhxt-ortho-hero-meta">
              <span>{Number(activeTask.assetCount || 0)} 张航片</span>
              <span className="dot">·</span>
              <span>
                {activeTask.renderAoi?.confirmedAt
                  ? `已框选 ${activeTask.renderAoi.selectedCount || '?'} 张`
                  : '尚未框选'}
              </span>
            </div>
            <p className="zhxt-ortho-hero-next">
              {['provisioning', 'modeling'].includes(activeTask.status)
                ? '建模进行中'
                : activeTask.status === 'awaiting_approval'
                  ? '等待确认，或填写验证码'
                  : Number(activeTask.assetCount || 0) < 1
                    ? '添加航拍照片开始'
                    : !activeTask.renderAoi?.confirmedAt
                      ? '在地图上框选渲染范围'
                      : '可以申请启动建模了'}
            </p>

            <div className="zhxt-ortho-primary-wrap">
              {['provisioning', 'modeling'].includes(activeTask.status) ? (
                <Button block type="primary" danger size="middle" loading={busy} onClick={onAbortModeling}>
                  中止建模
                </Button>
              ) : activeTask.status === 'awaiting_approval' ? (
                <div className="zhxt-ortho-code-row">
                  <Input
                    size="middle"
                    placeholder="6 位验证码"
                    value={code}
                    maxLength={6}
                    onChange={(e) => onCodeChange(e.target.value.replace(/\D/g, ''))}
                  />
                  <Button type="primary" size="middle" loading={busy} onClick={onConfirmCode}>确认</Button>
                </div>
              ) : Number(activeTask.assetCount || 0) < 1 ? (
                <Button
                  block
                  type="primary"
                  size="middle"
                  disabled={busy}
                  onClick={() => {
                    holdPanelOpenRef.current = true;
                    onOpenAssets();
                  }}
                >
                  添加航片
                </Button>
              ) : !activeTask.renderAoi?.confirmedAt ? (
                <Button
                  block
                  type="primary"
                  size="middle"
                  disabled={busy || !['draft', 'ready', 'failed', 'succeeded'].includes(activeTask.status)}
                  onClick={() => onStartRenderAoi({ skipModal: false })}
                >
                  框选渲染区
                </Button>
              ) : (
                <Button
                  block
                  type="primary"
                  size="middle"
                  disabled={busy || !['ready', 'failed', 'succeeded'].includes(activeTask.status)}
                  onClick={() => onRequestStart(false)}
                >
                  申请启动建模
                </Button>
              )}
            </div>

            {!['provisioning', 'modeling'].includes(activeTask.status) && (
              <div className="zhxt-ortho-soft-links">
                <button
                  type="button"
                  className="zhxt-ortho-link"
                  disabled={busy}
                  onClick={() => {
                    holdPanelOpenRef.current = true;
                    onOpenAssets();
                  }}
                >
                  管理素材
                </button>
                {Number(activeTask.assetCount || 0) > 0
                  && activeTask.renderAoi?.confirmedAt
                  && (
                  <button
                    type="button"
                    className="zhxt-ortho-link"
                    disabled={busy || !['draft', 'ready', 'failed', 'awaiting_approval', 'succeeded'].includes(activeTask.status)}
                    onClick={() => onStartRenderAoi({ skipModal: true })}
                  >
                    重新框选
                  </button>
                )}
                <label className="zhxt-ortho-link zhxt-ortho-link-check">
                  <Checkbox
                    checked={wantGlb}
                    onChange={(e) => onWantGlbChange(e.target.checked)}
                  />
                  <span>含 3D</span>
                </label>
                <label className="zhxt-ortho-link zhxt-ortho-link-check" title="成功后删除 OSS 中转航片，只保留正射/3D 成果">
                  <Checkbox
                    checked={purgeInboxOnSuccess}
                    onChange={(e) => onPurgeChange(e.target.checked)}
                  />
                  <span>清中转</span>
                </label>
                {activeTask.status === 'draft' && (
                  <button
                    type="button"
                    className="zhxt-ortho-link"
                    disabled={busy}
                    onClick={onMarkReady}
                  >
                    标记就绪
                  </button>
                )}
                {activeTask.status === 'ready' && (
                  <button
                    type="button"
                    className="zhxt-ortho-link"
                    disabled={busy}
                    onClick={onUnready}
                  >
                    撤回就绪
                  </button>
                )}
                {activeTask.status === 'awaiting_approval' && (
                  <button
                    type="button"
                    className="zhxt-ortho-link"
                    disabled={busy}
                    onClick={onWithdraw}
                  >
                    撤销申请
                  </button>
                )}
                {['succeeded', 'failed', 'ready', 'awaiting_approval', 'cancelled'].includes(activeTask.status) && (
                  <button
                    type="button"
                    className="zhxt-ortho-link zhxt-ortho-link--warn"
                    disabled={busy}
                    onClick={onPrepareRemodel}
                  >
                    重建模
                  </button>
                )}
                {Number(activeTask.assetCount || 0) > 0
                  && ['draft', 'ready', 'failed', 'awaiting_approval'].includes(activeTask.status) && (
                  <button
                    type="button"
                    className="zhxt-ortho-link zhxt-ortho-link--danger"
                    disabled={busy}
                    onClick={onClearAssets}
                  >
                    清空航片
                  </button>
                )}
              </div>
            )}
          </div>

          <OrthoAssetsModal
            open={assetsModalOpen}
            task={activeTask}
            onClose={() => {
              onCloseAssets();
              holdPanelOpenRef.current = false;
            }}
            onChanged={onAssetsChanged}
          />
        </>
      )}
    </Modal>
  );
}
